export interface Settings {
	ngramMin: number
	ngramMax: number
	wordNgrams: number
	hashSize: number
	lowercase: boolean
	/** Only used in training; smaller weights are stored as 0. */
	minWeight: number
}

const FNV_OFFSET_BASIS = 0x811c9dc5
const FNV_PRIME = 0x01000193

const WHITESPACE = '\\u0009-\\u000D\\u0020\\u00A0\\u1680\\u2000-\\u200A\\u2028\\u2029\\u202F\\u205F\\u3000\\uFEFF'
const LETTER = '\\p{L}\\p{M}'
const WORD_CHARACTER = `${LETTER}\\p{N}`
const JOINER = "'\\u2019\\u2010\\-"

/**
 * A word is a run of letters, combining marks and digits, kept whole across an
 * apostrophe or hyphen ("O'Neill", "part-time", "covid-19") unless it sits between two
 * digits, so a year range still gives each year as a word. Any other character that is
 * not whitespace ("@", "£", "–") is a word of its own.
 */
const WORD_PATTERN_SOURCE = (
	''
	+ `[${WORD_CHARACTER}]+`
	// ^ a run of letters, combining marks and digits: "o", "part", "2019"
	+ `(?:`
	// ^ then, any number of times ("stoke-on-trent"):
	+ `(?:(?<=[${LETTER}])[${JOINER}]`
	// ^ an apostrophe or hyphen after a letter: "o'neill", "covid-19"
	+ `|[${JOINER}](?=\\p{L}))`
	// ^ or one before a letter: "19-year-old", so only "2019-2023" splits
	+ `[${WORD_CHARACTER}]+`
	// ^ the next run: "neill", "19", "year"
	+ `)*`
	+ `|[^${WORD_CHARACTER}${WHITESPACE}]`
	// ^ or one character that is neither part of a word nor whitespace: "@", "£", "–"
)

const WORD_PATTERN = new RegExp(WORD_PATTERN_SOURCE, 'gu')

const WORD_FEATURE = '\u0001'
const TEXT_START = '\u0002'
const TEXT_END = '\u0003'

const encoder = new TextEncoder()

/**
 * Must match Featuriser.php exactly, or the model's weights are read for the wrong
 * features without any error. Connectors (lowercase) are dropped from the end of the text,
 * since a text ending in one ("Baker at") is still being written.
 */
export function indices(text: string, settings: Settings, connectors: readonly string[] = []): number[] {
	const words = wordsOf(text, settings, connectors)
	const features: string[] = []

	for (const word of words) {
		const characters = Array.from(`<${word}>`)

		for (let size = settings.ngramMin; size <= settings.ngramMax; size++) {
			for (let start = 0; start + size <= characters.length; start++) {
				features.push(characters.slice(start, start + size).join(''))
			}
		}
	}

	const sequence = [TEXT_START, ...words, TEXT_END]
	for (let size = 1; size <= settings.wordNgrams; size++) {
		for (let start = 0; start + size <= sequence.length; start++) {
			features.push(WORD_FEATURE + sequence.slice(start, start + size).join(' '))
		}
	}

	return [...new Set(features.map((feature) => fnv1a(feature) % settings.hashSize))].sort((first, second) =>
		first - second
	)
}

/**
 * The words the text is scored on, without trailing connectors. Matches Featuriser::words().
 */
export function wordsOf(text: string, settings: Settings, connectors: readonly string[] = []): string[] {
	const trimmed = text.trim()
	const words = [...((settings.lowercase ? trimmed.toLowerCase() : trimmed).match(WORD_PATTERN) ?? [])]

	while (words.length > 0 && connectors.includes(words[words.length - 1].toLowerCase())) {
		words.pop()
	}

	return words
}

export function fnv1a(text: string): number {
	let hash = FNV_OFFSET_BASIS

	for (const byte of encoder.encode(text)) {
		hash ^= byte
		hash = Math.imul(hash, FNV_PRIME) >>> 0
	}

	return hash
}
